/**
 * Opens a Shopify Forms inline form inside a native <dialog>.
 *
 * Shopify Forms is added to a template as its own "Apps" section, and its
 * content lives in a shadow root, so we relocate the host element into the
 * dialog rather than trying to render or restyle it. Moving a node preserves
 * its shadow root and listeners, so the form keeps working.
 *
 * showModal() supplies focus trapping, Esc, and background inerting.
 */
(() => {
  const FOUND = 'data-forms-modal-adopted';

  const findForm = (root) => {
    const formId = root.dataset.formsModalFormId;
    const custom = root.dataset.formsModalSelector;
    const selectors = [
      custom,
      'shopify-forms',
      '[id*="shopify-forms"]',
      '[class*="shopify-forms"]',
      formId && `[id*="${formId}"]`,
      formId && `[data-form-id="${formId}"]`,
    ].filter(Boolean);

    for (const selector of selectors) {
      for (const el of document.querySelectorAll(selector)) {
        // Ignore anything already inside our own dialog.
        if (!el.closest('[data-forms-modal]')) return el;
      }
    }
    return null;
  };

  const adopt = (root, body) => {
    if (root.hasAttribute(FOUND)) return true;
    const form = findForm(root);
    if (!form) return false;

    // Hide the section the form arrived in so it does not also render inline.
    const section = form.closest('.shopify-section');
    if (section && !section.contains(root)) section.style.display = 'none';

    body.replaceChildren(form);
    form.style.display = '';
    root.setAttribute(FOUND, 'true');
    return true;
  };

  const init = (root) => {
    if (root.dataset.formsModalReady === 'true') return;
    root.dataset.formsModalReady = 'true';

    const dialog = root.querySelector('[data-forms-modal]');
    const opener = root.querySelector('[data-forms-modal-open]');
    const body = root.querySelector('[data-forms-modal-body]');
    if (!dialog || !opener || !body || typeof dialog.showModal !== 'function') return;

    // The app injects its form asynchronously, so keep watching until it lands.
    if (!adopt(root, body)) {
      const observer = new MutationObserver(() => {
        if (adopt(root, body)) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 15000);
    }

    // Not all close paths emit a 'close' event here, so restoring scroll and
    // focus runs explicitly rather than hanging off that event alone. Leaving
    // the body locked would strand the whole page.
    const openModal = () => {
      adopt(root, body);
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      // Nudge anything listening for layout changes to re-measure now that the
      // dialog actually has dimensions.
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };

    const closeModal = () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = '';
      opener.focus();
    };

    opener.addEventListener('click', openModal);
    dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
    });

    dialog.querySelector('[data-forms-modal-close]')?.addEventListener('click', closeModal);

    // <dialog> closes on Escape natively, but the embedded form can consume the
    // key first. Listen on the document in the capture phase so the fallback
    // runs wherever focus happens to be, then let close() de-duplicate.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && dialog.open) {
        event.preventDefault();
        closeModal();
      }
    }, true);

    // Clicks land on the dialog itself only when they hit the backdrop.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeModal();
    });
  };

  const initAll = () => document.querySelectorAll('[data-forms-modal-root]').forEach(init);

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  initAll();
})();
